"use client";
import { useReactiveVar } from "@apollo/client/react";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";

import { ClientErrorMessage } from "@portal/app/_common/lib/clientErrorUtils";
import { createGenericContext } from "@portal/app/_common/lib/createGenericContext";
import { accessTokenVar } from "../../apolloUtils";
import { LOCAL_STORAGE_KEYS, useLocalStorage } from "@portal/app/_components/hooks/useLocalStorage";
import { Email } from "@portal/app/login/_components/pages/Email";
import { Code } from "@portal/app/login/_components/pages/Code";
import { GoogleAuthForSignIn } from "@portal/app/login/_components/GoogleAuthForSignIn";
import { Text } from "@portal/app/login/_components/pages/Text";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMobile } from "@fortawesome/pro-regular-svg-icons/faMobile";
import { faEnvelope } from "@fortawesome/pro-duotone-svg-icons/faEnvelope";
import { useQueryParams } from "@portal/app/_components/hooks/useQueryParams";
import { useTheme } from "@mui/material/styles";
import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons/faArrowLeft";
import { gql } from "graphql-tag";
import { SignInType } from "@portal/app/login/SignInFlowController";
import { DreamMuiLargeListItem } from "@portal/app/_components/ui-elements/Mui/DreamMuiLargeListItem";
import { AppleAuthForSignIn } from "@portal/app/login/_components/AppleAuthForSignIn";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight";
import { LOGIN_QUERY_PARAMS } from "@portal/app/_common/lib/token";
import { ContactDisclaimer } from "../../ui-elements/ContactDisclaimer";
import { usePageContext } from "../../context/PageContext";

void gql`
  mutation createUserSignInStep(
    $data: UserCreateInput!
    $programFlow: Boolean!
    $programId: ID
    $questionnaireFlow: Boolean!
    $skipperFlow: Boolean!
    $flowIntent: FlowIntent
    $signInFlow: Boolean!
  ) {
    createOneUser(data: $data) {
      id
      phone
      phoneVerified
      email
      emailVerified
      customer {
        id
        user {
          id
          nameFirst
          nameLast
          email
          phone
        }
        ...FlowStateUpdateFragment
      }
    }
  }
`;

export type Page = "SELECT_METHOD" | "EMAIL" | "PHONE" | "CODE";
export type SignInMethod = "t" | "e" | "g" | "a";
export const SIGN_IN_METHOD: Record<string, SignInMethod> = {
  TEXT: "t",
  EMAIL: "e",
  GOOGLE: "g",
  APPLE: "a",
};

const [useSignInContext, SignInContext] = createGenericContext<{
  page: Page;
  setPage: React.Dispatch<React.SetStateAction<Page>>;
  setClientErrorMessage: React.Dispatch<React.SetStateAction<ClientErrorMessage | null>>;
  handleReset: () => void;
}>("SignInContext");
export { SignInContext, useSignInContext };

const MAGIC_LINK_SENT_KEY = "login_magicLinkSent";

export const NO_SHARED_LOGINS_ERROR: ClientErrorMessage = {
  title: "Do not use a shared @dreambound.com account",
  severity: "warning",
  description:
    "Using shared logins prevents us from having an accurate audit trail. Please use your own @dreambound.com account.",
};

export const SignInStep = ({ signInType: initialSignInType }: { signInType: SignInType }) => {
  const router = useRouter();
  const theme = useTheme();
  const [page, setPage] = useState<Page>("SELECT_METHOD");
  const [clientErrorMessage, setClientErrorMessage] = useState<ClientErrorMessage | null>(null);
  const accessToken = useReactiveVar(accessTokenVar);
  const {} = usePageContext();
  const { getQueryParam, removeQueryParam } = useQueryParams();
  const routeCode = getQueryParam(LOGIN_QUERY_PARAMS.CODE);
  const routePhone = getQueryParam(LOGIN_QUERY_PARAMS.PHONE);
  const routeEmail = getQueryParam(LOGIN_QUERY_PARAMS.EMAIL);
  const [method, setMethod, removeMethod] = useLocalStorage<string>(
    LOCAL_STORAGE_KEYS.MAGIC_LINK_METHOD
  );
  const [, setEmail, removeEmail] = useLocalStorage<string>(LOCAL_STORAGE_KEYS.MAGIC_LINK_EMAIL);
  const [, setPhone, removePhone] = useLocalStorage<string>(LOCAL_STORAGE_KEYS.MAGIC_LINK_PHONE);
  const [, setCode, removeCode] = useLocalStorage<string>(LOCAL_STORAGE_KEYS.MAGIC_LINK_CODE);
  const [codeSent, , removeCodeSent] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.MAGIC_LINK_CODE_SENT
  );
  const [signInType, setSignInType] = useState(initialSignInType);

  const handleReset = useCallback(() => {
    setClientErrorMessage(null);
    removeMethod();
    removeEmail();
    removePhone();
    removeCode();
    removeCodeSent();
    localStorage.removeItem(MAGIC_LINK_SENT_KEY);
    setPage("SELECT_METHOD");
  }, [removeMethod, removeEmail, removePhone, removeCode, removeCodeSent]);

  useEffect(() => {
    /**
     * Component initialization -- set phone / email from route or localStorage
     */
    if (codeSent) {
      setPage("CODE");
    } else if (routeCode && (routeEmail || routePhone)) {
      setCode(routeCode);
      if (routePhone) {
        setPhone(routePhone);
      } else {
        removePhone();
      }
      if (routeEmail) {
        setEmail(routeEmail);
      } else {
        removeEmail();
      }
      if (routeEmail || routePhone) {
        setMethod(routePhone ? SIGN_IN_METHOD.TEXT : SIGN_IN_METHOD.EMAIL);
      }
      setPage("CODE");
    }
  }, [
    codeSent,
    removeEmail,
    removePhone,
    routeCode,
    routeEmail,
    routePhone,
    setCode,
    setEmail,
    setMethod,
    setPhone,
  ]);

  useEffect(() => {
    /**
     * If we're authenticated already, clean up the params used for logging in
     */
    if (!!accessToken && (routeCode || routeEmail || routePhone)) {
      removeQueryParam(LOGIN_QUERY_PARAMS.CODE);
      removeQueryParam(LOGIN_QUERY_PARAMS.EMAIL);
      removeQueryParam(LOGIN_QUERY_PARAMS.PHONE);
    }
  }, [accessToken, removeQueryParam, routeCode, routeEmail, routePhone, router]);

  useEffect(() => {
    /**
     * Whenever we change pages, reset the error message
     */
    setClientErrorMessage(null);
  }, [page]);

  return (
    <SignInContext.Provider
      value={{
        page,
        setPage,
        setClientErrorMessage,
        handleReset,
      }}
    >
      <Box
        sx={{ display: "flex", flexDirection: "column", flex: 1, justifyContent: "space-between" }}
      >
        <Box>
          {page === "SELECT_METHOD" ? (
            <Box>
              <Typography variant="page_title">
                {signInType === "CREATE_ACCOUNT" ? "Create an account" : "Welcome back"}
              </Typography>
              <DreamMuiLargeListItem
                title={
                  signInType === "CREATE_ACCOUNT"
                    ? "Use my phone number"
                    : "Sign in with phone number"
                }
                icon={<FontAwesomeIcon icon={faMobile} />}
                endIcon={<FontAwesomeIcon icon={faChevronRight} size="xs" />}
                variant="clickable"
                data-testid="login-with-phone"
                onClick={() => {
                  setMethod(SIGN_IN_METHOD.TEXT);
                  setPage("PHONE");
                }}
              />
              <DreamMuiLargeListItem
                title={signInType === "CREATE_ACCOUNT" ? "Use my email" : "Sign in with email"}
                icon={<FontAwesomeIcon icon={faEnvelope} />}
                endIcon={<FontAwesomeIcon icon={faChevronRight} size="xs" />}
                variant="clickable"
                data-testid="login-with-email"
                onClick={() => {
                  setMethod(SIGN_IN_METHOD.EMAIL);
                  setPage("EMAIL");
                }}
              />
              <GoogleAuthForSignIn
                signInType={signInType}
                propsToContainer={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />
              <AppleAuthForSignIn
                signInType={signInType}
                propsToContainer={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mt: 2,
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mt: 1,
                }}
              >
                <Typography>
                  {signInType === "CREATE_ACCOUNT"
                    ? "Already registered?"
                    : "Don't have an account?"}
                </Typography>
                <Button
                  variant="text"
                  onClick={() => {
                    setSignInType(signInType === "CREATE_ACCOUNT" ? "SIGN_IN" : "CREATE_ACCOUNT");
                  }}
                >
                  {signInType === "CREATE_ACCOUNT" ? "Sign in" : "Create an account"}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box display="flex" alignItems="center">
              <IconButton
                onClick={() => {
                  setPage("SELECT_METHOD");
                }}
                size="small"
              >
                <FontAwesomeIcon icon={faArrowLeft} color={theme.palette.primary.main} />
              </IconButton>
              <Typography variant="new_h2" component="span" sx={{ m: 0, ml: 2 }}>
                {(() => {
                  switch (page) {
                    case "EMAIL":
                      return "Use my email";
                    case "PHONE":
                      return "Use my phone number";
                    case "CODE":
                      return method === SIGN_IN_METHOD.TEXT
                        ? "We've texted you a code"
                        : method === SIGN_IN_METHOD.EMAIL
                          ? "We've emailed you a code"
                          : "We've sent you a code";
                  }
                })()}
              </Typography>
            </Box>
          )}
          {clientErrorMessage && (
            <Alert
              severity={clientErrorMessage.severity ?? "error"}
              data-testid="login-error"
              sx={{ mt: 2 }}
            >
              <AlertTitle>{clientErrorMessage.title}</AlertTitle>
              {clientErrorMessage.description}
            </Alert>
          )}
          {page === "EMAIL" && <Email />}
          {page === "CODE" && <Code />}
          {page === "PHONE" && <Text />}
        </Box>
        {page === "EMAIL" ? (
          <ContactDisclaimer type="EMAIL" />
        ) : page === "PHONE" ? (
          <ContactDisclaimer type="PHONE" />
        ) : (
          <ContactDisclaimer type="ALL" />
        )}
      </Box>
    </SignInContext.Provider>
  );
};
